import PropTypes from 'prop-types'
import { useEffect, useState } from 'react'
import { format } from 'date-fns'
import clsx from 'clsx'
import useTranslations from '../../../i18n/useTranslations'

import { PICKUP_OPTIONS } from '../../../context/ShippingSend/Step1Origin/Pickup'
import useAvailableHours from '../../../context/ShippingSend/Step1Origin/useAvailableHours'
import ChoosePickup, {
  formatPickupId,
} from '../../../context/ShippingSend/Step1Origin/Pickup/ChoosePickup'
import {
  calculateSchedule,
  calculateScheduleOptions,
  calculateTimezoneOptionsByDay,
} from '../../../context/ShippingSend/Step1Origin'
import {
  useShippingUpdateMutation,
  useInvalidateShippingsTracking,
} from '../../../services/shippingSend'
import { useInvalidateShippingsDetail } from '../../../services/shippingsTracking'

import { Alert, Box, Button, Typography } from '@mui/material'
import { ReactComponent as RocketLaunch } from '../../../assets/icons/rocket-launch.svg'
import { ReactComponent as Clock } from '../../../assets/icons/clock.svg'
import { ReactComponent as CalendarBlank } from '../../../assets/icons/calendar-blank.svg'
import { ReactComponent as ArrowRight } from '../../../assets/icons/arrow-right.svg'
import { ReactComponent as Info } from '../../../assets/icons/warning-circle.svg'
import useStyles from './styles'
import DrawerWrapper from '../DrawerWrapper'
import { useUserContext } from '../../../auth/UserProvider'

const ScheduledPickupDrawer = ({
  shippingsInfo,
  onClickClose,
  onClickConfirm,
}) => {
  const t = useTranslations()
  const classes = useStyles()
  const { centres } = useUserContext()

  const [pickupSource, setPickupSource] = useState(PICKUP_OPTIONS.asap)
  const [pickupDate, setPickupDate] = useState(null)
  const [selectedDay, setSelectedDay] = useState('')
  const [selectedSchedule, setSelectedSchedule] = useState('none')
  const [selectedTimezone, setSelectedTimezone] = useState('none')

  // events: update scheduled pickup
  const {
    mutate,
    isLoading: isMutationLoading,
    isSuccess: isMutationSuccess,
    reset: resetMutation,
  } = useShippingUpdateMutation()

  const onClickUpdate = () => {
    const [minTime, maxTime] = pickupDate.split('/')

    mutate({
      hasPickupAsap: pickupSource === PICKUP_OPTIONS.asap,
      date: minTime,
      minTime,
      maxTime,
      shippingCodes: shippingsInfo.map(shipping => shipping.shippingCode),
    })
  }

  // events: close drawer
  const invalidateShippingTracking = useInvalidateShippingsTracking()
  const invalidateShippingsDetail = useInvalidateShippingsDetail()

  const handleClose = () => {
    if (isMutationSuccess) {
      invalidateShippingTracking()
      shippingsInfo.forEach(shipping =>
        invalidateShippingsDetail(shipping.shippingCode),
      )
      if (onClickConfirm) {
        onClickConfirm()
      }
    }
    onClickClose()
  }

  // get available hours
  const { data: availableHours, isLoading: isAvailableHoursLoading } =
    useAvailableHours(
      shippingsInfo[0]?.originPostalCode,
      format(new Date(), 'dd-MM-yyyy'),
    )

  function normalizeString(str) {
    return str
      .replace(/[^a-zA-Z0-9 ]/g, '')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase()
  }

  const hasFixedPickupCentre = centres.find(centre =>
    shippingsInfo.some(
      shipping =>
        centre.id === shipping.clientCenterCode &&
        centre.fixedPickupsAdresses &&
        centre.fixedPickupsAdresses.length > 0 &&
        normalizeString(centre.fixedPickupsAdresses[0].address) ===
          normalizeString(shipping.senderAddress) &&
        centre.fixedPickupsAdresses[0].postalCode === shipping.originPostalCode,
    ),
  )

  const hasFixedPickupTimeZone = hasFixedPickupCentre !== undefined

  // ensure pickup date
  useEffect(() => {
    const getAsapPickupDate = () => {
      if (availableHours?.length) {
        const firstAvailableDay = availableHours[0]
        const firstAvailableHour = firstAvailableDay[0].minDate
        const lastAvailableHour =
          firstAvailableDay[firstAvailableDay.length - 1].maxDate
        return `${firstAvailableHour}/${lastAvailableHour}`
      }
      return null
    }

    const getFixedPickupDate = () => {
      if (!hasFixedPickupCentre) return null

      const today = new Date()
      const dayOfWeek = today.getDay()
      const times = hasFixedPickupCentre.fixedPickupsAdresses[0].timeZones

      for (let i = dayOfWeek; i < dayOfWeek + 7; i++) {
        const dayIndex = i % 7
        const dayTimes = times[dayIndex]
        if (dayTimes.length > 0) {
          const { fromHour, toHour } = dayTimes[0]
          const fromTimeParts = fromHour.split(':').map(Number)
          const toTimeParts = toHour.split(':').map(Number)

          const fromTimeToday = new Date(
            today.getFullYear(),
            today.getMonth(),
            today.getDate(),
            fromTimeParts[0],
            fromTimeParts[1],
          )
          const toTimeToday = new Date(
            today.getFullYear(),
            today.getMonth(),
            today.getDate(),
            toTimeParts[0],
            toTimeParts[1],
          )

          if ((i === dayOfWeek && today < toTimeToday) || i > dayOfWeek) {
            const formattedFrom = format(fromTimeToday, 'yyyy-MM-dd HH:mm')
            const formattedTo = format(toTimeToday, 'yyyy-MM-dd HH:mm')
            return `${formattedFrom}/${formattedTo}`
          }
        }
      }
      return null
    }

    if (!isAvailableHoursLoading) {
      if (hasFixedPickupTimeZone) {
        setPickupDate(getFixedPickupDate())
      } else if (pickupSource === PICKUP_OPTIONS.asap) {
        setPickupDate(getAsapPickupDate())
      } else {
        setPickupDate(null)
      }
    }
  }, [
    isAvailableHoursLoading,
    pickupSource,
    shippingsInfo?.length,
    centres,
    hasFixedPickupTimeZone,
  ])

  useEffect(() => {
    if (shippingsInfo?.length === 0) {
      setPickupSource(PICKUP_OPTIONS.asap)
      setPickupDate(null)
      setSelectedDay('')
      setSelectedSchedule('none')
      setSelectedTimezone('none')
      resetMutation()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shippingsInfo?.length])

  // render helpers

  const renderTitle = () => {
    return (
      <Typography
        variant="h1"
        className={clsx('secondaryTitle', 'decorator', classes.title)}
      >
        {t('unscheduled-pickup:main-title')}
      </Typography>
    )
  }

  const renderSubtitle = () => {
    return !isMutationSuccess ? (
      <Typography variant="h2" mb={2}>
        {t('unscheduled-pickup:main-subtitle')}
      </Typography>
    ) : null
  }

  const renderPickupButtons = () => {
    return !isMutationSuccess ? (
      <Box mb={2}>
        <Button
          size="small"
          startIcon={<RocketLaunch weight="regular" />}
          disabled={isAvailableHoursLoading || availableHours?.length === 0}
          variant={
            pickupSource === PICKUP_OPTIONS.asap ? 'contained' : 'outlined'
          }
          onClick={() => setPickupSource(PICKUP_OPTIONS.asap)}
        >
          {t(!!hasFixedPickupTimeZone ? 'message:fixedPickup' : 'message:asap')}
        </Button>
        <Button
          disabled={isAvailableHoursLoading || availableHours?.length === 0}
          size="small"
          startIcon={<Clock weight="regular" />}
          variant={
            pickupSource === PICKUP_OPTIONS.chooseDayAndHour
              ? 'contained'
              : 'outlined'
          }
          onClick={() => setPickupSource(PICKUP_OPTIONS.chooseDayAndHour)}
        >
          {t('unscheduled-pickup:option:chosse-day-and-hour')}
        </Button>
      </Box>
    ) : null
  }

  const renderAsapDate = () => {
    if (
      hasFixedPickupTimeZone &&
      !isMutationSuccess &&
      availableHours?.length &&
      pickupSource === PICKUP_OPTIONS.asap &&
      pickupDate
    ) {
      return (
        <Alert
          severity="success"
          icon={<CalendarBlank />}
          className={classes.asapAlert}
        >
          <Typography variant="h3medium">
            {t('shipping-send-form:pickup-date', formatPickupId(pickupDate))}
          </Typography>
          <Typography variant="subtitle1">
            {t('unscheduled-pickup:asap-info')}
          </Typography>
        </Alert>
      )
    } else if (
      !isMutationSuccess &&
      availableHours?.length &&
      pickupSource === PICKUP_OPTIONS.asap &&
      pickupDate
    ) {
      return (
        <Alert
          severity="success"
          icon={<CalendarBlank />}
          className={classes.asapAlert}
        >
          <Typography variant="h3medium">
            {t('shipping-send-form:pickup-date', formatPickupId(pickupDate))}
          </Typography>
          <Typography variant="subtitle1">
            {t('unscheduled-pickup:asap-info')}
          </Typography>
        </Alert>
      )
    }
  }

  const renderChossePickup = () => {
    const getEnabledCalendarDays = () =>
      availableHours.map(day => format(day[0].minDate, 'yyyy-MM-dd'))

    const getFirstPickupDate = () =>
      `${availableHours[0][0].minDate}/${availableHours[0][0].maxDate}`

    const getScheduleOptions = () => {
      const schedules =
        availableHours && selectedDay !== ''
          ? calculateSchedule(selectedDay, availableHours)
          : []
      return calculateScheduleOptions(schedules)
    }

    const getTimezoneOptions = () =>
      availableHours && selectedDay !== ''
        ? calculateTimezoneOptionsByDay(selectedDay, availableHours)
        : []

    if (
      !isMutationSuccess &&
      availableHours?.length &&
      availableHours &&
      pickupSource === PICKUP_OPTIONS.chooseDayAndHour
    ) {
      return (
        <Box mb={2}>
          <ChoosePickup
            isLoading={isAvailableHoursLoading}
            enabledCalendarDays={getEnabledCalendarDays()}
            scheduleOptions={getScheduleOptions()}
            timezoneOptions={getTimezoneOptions()}
            selectedSchedule={selectedSchedule}
            selectedDay={selectedDay}
            selectedTimezone={selectedTimezone}
            pickupDate={pickupDate}
            firstPickupDate={getFirstPickupDate()}
            changeDay={setSelectedDay}
            changeSchedule={setSelectedSchedule}
            changeTimezone={setSelectedTimezone}
            changePickupDate={setPickupDate}
            hasFixedPickup={!!hasFixedPickupTimeZone}
          />
        </Box>
      )
    }
  }

  const renderReminder = () => {
    return !isMutationSuccess && availableHours?.length ? (
      <Alert severity="info" icon={<Info />} className={classes.reminderAlert}>
        <Typography variant="subtitle1">
          {t('unscheduled-pickup:reminder')}
        </Typography>
      </Alert>
    ) : null
  }

  const renderActionButtons = () => {
    return !isMutationSuccess && availableHours?.length ? (
      <Box className={classes.buttonsWrapper}>
        <Button
          size="small"
          disabled={isAvailableHoursLoading || isMutationLoading}
          variant="outlined"
          onClick={onClickClose}
        >
          {t('action:cancel')}
        </Button>

        <Button
          size="small"
          disabled={
            isAvailableHoursLoading ||
            availableHours?.length === 0 ||
            pickupDate === null ||
            isMutationLoading
          }
          endIcon={<ArrowRight />}
          onClick={onClickUpdate}
        >
          {t('action:save-changes')}
        </Button>
      </Box>
    ) : null
  }

  const renderConfirmation = () => {
    return isMutationSuccess ? (
      <Alert severity="success" className={classes.asapAlert}>
        <Typography variant="h3medium">
          {t('unscheduled-pickup:confirmation-title')}
        </Typography>
        <Typography variant="subtitle1">
          {t('unscheduled-pickup:confirmation-subtitle')}
        </Typography>
      </Alert>
    ) : null
  }

  const renderConfirmButtons = () => {
    return isMutationSuccess ? (
      <Box className={classes.buttonsWrapper}>
        <Button size="small" endIcon={<ArrowRight />} onClick={handleClose}>
          {t('unscheduled:success:action:confirm')}
        </Button>
      </Box>
    ) : null
  }

  return (
    <DrawerWrapper isOpen={!!shippingsInfo?.length} onClose={handleClose}>
      {renderTitle()}
      {renderSubtitle()}
      {renderPickupButtons()}
      {renderAsapDate()}
      {renderChossePickup()}
      {renderReminder()}
      {renderActionButtons()}
      {renderConfirmation()}
      {renderConfirmButtons()}
    </DrawerWrapper>
  )
}

ScheduledPickupDrawer.propTypes = {
  shippingsInfo: PropTypes.arrayOf(
    PropTypes.shape({
      shippingCode: PropTypes.string.isRequired,
      originPostalCode: PropTypes.string.isRequired,
    }),
  ),
  onClickClose: PropTypes.func.isRequired,
  onClickConfirm: PropTypes.func,
}

export default ScheduledPickupDrawer
